import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Row, Col, Select, Typography, Spin, notification, Table } from "antd";
import { CensusShopService } from "../../Services";
import userServices from "../../UserServices/UserServices";
import { get, pick, sample } from "lodash";
import { GoogleMap } from "../Common";
import { getPosition } from "../Common/Config";

const { Title } = Typography;

const keysToFetch = [
	"_id",
	"shop_details.name",
	"shop_details.coordinates",
	"shop_data.population_score",
	"shop_data.sec_score",
	"shop_data.size_score",
	"shop_data.channel_score",
	"shop_data.footfall_score"
];

const factors = ["Population", "SEC", "Channel Type", "Building Size", "Busiest time"];
const percentOptions = Array.from({ length: 21 }, (_, i) => ({
	label: `${i * 5}%`,
	value: i * 5
}));

// FactorSelector Component to handle percentage selection independently
const FactorSelector = ({ factor, value, onChange }) => {
	return (
		<Col span={4} key={factor}>
			<div style={{ marginBottom: 4 }}>
				<strong>{factor}</strong>
			</div>
			<Select
				style={{ width: "100%" }}
				options={percentOptions}
				value={value}
				onChange={newValue => onChange(factor, newValue)}
			/>
		</Col>
	);
};

const StoreScoring = () => {
	const { tenant } = pick(userServices.getCurrentUser(), ["tenant"]);

	const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
	// const [shops, setShops] = useState([]);
	const [merkersForMap, setMarkersForMap] = useState([]);
	const [shopsLoading, setShopsLoading] = useState(false);
	const [weights, setWeights] = useState({
		Population: 20,
		SEC: 20,
		"Channel Type": 20,
		"Building Size": 20,
		"Busiest time": 20
	});

	const calculateScore = useCallback((shops, weights) => {
		const calculatedData = shops.map(shop => {
			const score =
				(weights["Population"] * get(shop, "shop_data.population_score", 0)) / 100 +
				(weights["SEC"] * get(shop, "shop_data.sec_score", 0)) / 100 +
				(weights["Channel Type"] * get(shop, "shop_data.channel_score", 0)) / 100 +
				(weights["Building Size"] * get(shop, "shop_data.size_score", 0)) / 100 +
				(weights["Busiest time"] * get(shop, "shop_data.footfall_score", 0)) / 100;
			return {
				...shop,
				score: score.toFixed(2)
			};
		});

		// Sort by score in descending order
		const sortedShops = calculatedData.sort((a, b) => b.score - a.score);
		setMarkersForMap(sortedShops); // Update markers with sorted shops
	}, []);

	const handleChange = useCallback(
		(key, newValue) => {
			const oldValue = weights[key];
			const diff = newValue - oldValue;

			const otherKeys = Object.keys(weights).filter(k => k !== key);
			const totalOther = otherKeys.reduce((acc, k) => acc + weights[k], 0);

			if (diff > 0 && totalOther === 0) {
				// Prevent increasing if others are 0
				return;
			}

			let newWeights = { ...weights, [key]: newValue };

			if (diff !== 0) {
				let adjusted = {};

				otherKeys.forEach(k => {
					const share = totalOther > 0 ? weights[k] / totalOther : 0;
					adjusted[k] = weights[k] - share * diff;
				});

				// Clamp between 0–100 and round
				otherKeys.forEach(k => {
					adjusted[k] = Math.max(0, Math.round(adjusted[k]));
				});

				// Recalculate total and apply correction
				const newTotal = newValue + Object.values(adjusted).reduce((a, b) => a + b, 0);
				const correction = 100 - newTotal;

				if (correction !== 0) {
					const targetKey = otherKeys.find(
						k => adjusted[k] + correction >= 0 && adjusted[k] + correction <= 100
					);
					if (targetKey) {
						adjusted[targetKey] += correction;
					}
				}

				newWeights = {
					[key]: newValue,
					...adjusted
				};
			}

			setWeights(newWeights);
			calculateScore(merkersForMap, newWeights); // Recalculate the score when weights change
		},
		[weights, merkersForMap, calculateScore]
	);

	useEffect(() => {
		const fetchShops = async () => {
			try {
				setShopsLoading(true);
				const { data } = await CensusShopService.getAllTenantShopForScoring({
					tenantName: tenant,
					keysToFetch
				});
				setShopsLoading(false);
				// setShops(data || []);

				const sampleShop = sample(data);

				const mapedShops = data.map(shop => ({
					position: getPosition(get(shop, "shop_details.coordinates")),
					isPoi: true,
					showInfo: false,
					id: shop._id,
					...shop
				}));

				setMarkersForMap(mapedShops);
				calculateScore(mapedShops, weights);

				setMapCenter(
					sampleShop ? getPosition(get(sampleShop, "shop_details.coordinates")) : { lat: 0, lng: 0 }
				);
			} catch (error) {
				setShopsLoading(false);
				notification.error({
					message: "Error",
					description: error.message
				});
			}
		};

		fetchShops();
	}, []);

	const memoizedMarkers = useMemo(() => merkersForMap, [merkersForMap]);
	const memoizedMapCenter = useMemo(() => mapCenter, [mapCenter]);

	return (
		<div style={{ padding: 24 }}>
			<Spin spinning={shopsLoading}>
				<Row justify="center">
					<Title level={3}>Store Scoring</Title>
				</Row>
				<Row gutter={[16, 16]}>
					{factors.map(factor => (
						<FactorSelector key={factor} factor={factor} value={weights[factor]} onChange={handleChange} />
					))}
				</Row>

				<Row>
					<Col span={24}>
						<GoogleMap
							key="abc"
							polygons={[]}
							center={memoizedMapCenter}
							markers={{ storeScoring: true, storeScoringMarkers: memoizedMarkers }}
							_markers={[]}
						/>
					</Col>
				</Row>
				<Row>
					<Col span={24}>
						<Table
							dataSource={merkersForMap}
							columns={[
								{ title: "Shop Name", dataIndex: ["shop_details", "name"] },
								// { title: "Population Score", dataIndex: ["shop_data", "population_score"] },
								// { title: "SEC Score", dataIndex: ["shop_data", "sec_score"] },
								// { title: "Channel Score", dataIndex: ["shop_data", "channel_score"] },
								// { title: "Size Score", dataIndex: ["shop_data", "size_score"] },
								// { title: ""Busiest time" Score", dataIndex: ["shop_data", "footfall_score"] },
								{ title: "Score", dataIndex: "score" }
							]}
							rowKey="id"
						/>
					</Col>
				</Row>
			</Spin>
		</div>
	);
};

export default StoreScoring;
